import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Pencil, ChevronDown } from 'lucide-react';
import Button from '../UI/Button';
import Badge from '../UI/Badge';
import MultiSelectDropdown from '../UI/MultiSelectDropdown';

const inputClass =
  'w-full h-9 px-2.5 bg-background border border-border text-foreground rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const labelClass = 'block text-xs text-muted-foreground/70 mb-1';

const STATUS_VARIANT = { running: 'success', stopped: 'neutral', paused: 'warning', error: 'danger' };

const OVERLAY_OPTIONS = [
  { value: 'ema20', label: 'EMA 20' },
  { value: 'ema50', label: 'EMA 50' },
  { value: 'sma200', label: 'SMA 200' },
  { value: 'bb', label: 'Bollinger' },
  { value: 'volume', label: 'Volume' },
];

export default function BacktestConfigPanel({
  strategies,
  selectedStrategyId,
  onStrategyChange,
  formData,
  onFormChange,
  onRun,
  isRunning,
  progress,
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const eligible = (strategies || []).filter((s) => s.status !== 'archived');
  const selected = eligible.find((s) => s.id === selectedStrategyId);

  useEffect(() => {
    if (!selected) return;
    const cap = parseFloat(selected.investment_cap);
    if (cap > 0 && !isNaN(cap)) {
      onFormChange('initialCapital', cap);
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const feeMode = formData.feeMode || 'fixed';

  /* ── Desktop layout (md+) ── */
  const desktopPanel = (
    <div className="hidden md:flex items-end gap-3 flex-wrap">
      {/* Left group: config fields */}
      <div className="flex items-end gap-3 flex-wrap flex-1 min-w-0">
        {/* Strategy */}
        <div className="min-w-[180px]">
          <label className={labelClass}>Strategy</label>
          <div className="flex items-center gap-1.5">
            <select
              value={selectedStrategyId || ''}
              onChange={(e) => onStrategyChange(e.target.value)}
              className={inputClass}
            >
              <option value="">Select a strategy...</option>
              {eligible.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.type}) — {s.status}
                </option>
              ))}
            </select>
            {selected && (
              <Badge variant={STATUS_VARIANT[selected.status] || 'neutral'} className="flex-shrink-0">
                {selected.status.toUpperCase()}
              </Badge>
            )}
          </div>
        </div>

        {/* From */}
        <div>
          <label className={labelClass}>From</label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => onFormChange('startDate', e.target.value)}
            className={inputClass}
          />
        </div>

        {/* To */}
        <div>
          <label className={labelClass}>To</label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => onFormChange('endDate', e.target.value)}
            className={inputClass}
          />
        </div>

        {/* Capital */}
        <div className="w-24">
          <label className={labelClass}>Capital</label>
          <input
            type="number"
            value={formData.initialCapital}
            onChange={(e) => onFormChange('initialCapital', parseFloat(e.target.value))}
            className={inputClass}
          />
        </div>

        {/* Interval */}
        <div className="w-20">
          <label className={labelClass}>Interval</label>
          <select
            value={formData.timeframe || '1d'}
            onChange={(e) => onFormChange('timeframe', e.target.value)}
            className={inputClass}
          >
            <option value="1m">1 min</option>
            <option value="5m">5 min</option>
            <option value="15m">15 min</option>
            <option value="1h">1 hour</option>
            <option value="1d">1 day</option>
          </select>
        </div>

        {/* Fee mode */}
        <div className="w-20">
          <label className={labelClass}>Fee Type</label>
          <select
            value={feeMode}
            onChange={(e) => onFormChange('feeMode', e.target.value)}
            className={inputClass}
          >
            <option value="fixed">Fixed $</option>
            <option value="percent">Percent %</option>
          </select>
        </div>

        {/* Maker fee */}
        <div className="w-[72px]">
          <label className={labelClass}>Maker</label>
          <input
            type="number"
            value={formData.makerFee ?? 0}
            onChange={(e) => onFormChange('makerFee', parseFloat(e.target.value) || 0)}
            step={feeMode === 'percent' ? '0.01' : '0.1'}
            min="0"
            className={inputClass}
            placeholder={feeMode === 'percent' ? '0.1' : '0'}
          />
        </div>

        {/* Taker fee */}
        <div className="w-[72px]">
          <label className={labelClass}>Taker</label>
          <input
            type="number"
            value={formData.takerFee ?? 0}
            onChange={(e) => onFormChange('takerFee', parseFloat(e.target.value) || 0)}
            step={feeMode === 'percent' ? '0.01' : '0.1'}
            min="0"
            className={inputClass}
            placeholder={feeMode === 'percent' ? '0.15' : '0'}
          />
        </div>
      </div>

      {/* Right group: edit, overlays, progress, run */}
      <div className="flex items-end gap-2 flex-shrink-0">
        {selected && selected.status !== 'running' && (
          <button
            type="button"
            onClick={() => navigate(`/strategies/builder/${selected.id}`)}
            className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        )}

        <MultiSelectDropdown
          label="Overlays"
          icon={Layers}
          options={OVERLAY_OPTIONS}
          selected={formData.overlays || ['ema20', 'volume']}
          onChange={(next) => onFormChange('overlays', next)}
        />

        {isRunning && (
          <div className="flex items-center gap-2">
            <div className="w-24 bg-border rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">{progress}%</span>
          </div>
        )}

        <Button
          size="sm"
          onClick={onRun}
          disabled={isRunning || !selectedStrategyId}
          className="h-9"
        >
          {isRunning ? 'Running...' : 'Run Backtest'}
        </Button>
      </div>
    </div>
  );

  /* ── Mobile layout (<md) — collapsible ── */
  const mobilePanel = (
    <div className="md:hidden space-y-3">
      {/* Always visible: strategy + run button */}
      <div className="space-y-2">
        <div>
          <label className={labelClass}>Strategy</label>
          <div className="flex items-center gap-1.5">
            <select
              value={selectedStrategyId || ''}
              onChange={(e) => onStrategyChange(e.target.value)}
              className={`${inputClass} flex-1`}
            >
              <option value="">Select a strategy...</option>
              {eligible.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.type}) — {s.status}
                </option>
              ))}
            </select>
            {selected && (
              <Badge variant={STATUS_VARIANT[selected.status] || 'neutral'} className="flex-shrink-0">
                {selected.status.toUpperCase()}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isRunning && (
            <div className="flex items-center gap-2 flex-1">
              <div className="flex-1 bg-border rounded-full h-1.5">
                <div
                  className="bg-primary h-1.5 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">{progress}%</span>
            </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
            {selected && selected.status !== 'running' && (
              <button
                type="button"
                onClick={() => navigate(`/strategies/builder/${selected.id}`)}
                className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}

            <Button
              size="sm"
              onClick={onRun}
              disabled={isRunning || !selectedStrategyId}
              className="h-9"
            >
              {isRunning ? 'Running...' : 'Run Backtest'}
            </Button>

            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Collapsible settings */}
      {expanded && (
        <div className="space-y-3 pt-2 border-t border-border">
          {/* Two-column grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* From */}
            <div>
              <label className={labelClass}>From</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => onFormChange('startDate', e.target.value)}
                className={inputClass}
              />
            </div>

            {/* To */}
            <div>
              <label className={labelClass}>To</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => onFormChange('endDate', e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Capital */}
            <div>
              <label className={labelClass}>Capital</label>
              <input
                type="number"
                value={formData.initialCapital}
                onChange={(e) => onFormChange('initialCapital', parseFloat(e.target.value))}
                className={inputClass}
              />
            </div>

            {/* Interval */}
            <div>
              <label className={labelClass}>Interval</label>
              <select
                value={formData.timeframe || '1d'}
                onChange={(e) => onFormChange('timeframe', e.target.value)}
                className={inputClass}
              >
                <option value="1m">1 min</option>
                <option value="5m">5 min</option>
                <option value="15m">15 min</option>
                <option value="1h">1 hour</option>
                <option value="1d">1 day</option>
              </select>
            </div>

            {/* Fee type */}
            <div>
              <label className={labelClass}>Fee Type</label>
              <select
                value={feeMode}
                onChange={(e) => onFormChange('feeMode', e.target.value)}
                className={inputClass}
              >
                <option value="fixed">Fixed $</option>
                <option value="percent">Percent %</option>
              </select>
            </div>

            {/* Maker fee */}
            <div>
              <label className={labelClass}>Maker Fee</label>
              <input
                type="number"
                value={formData.makerFee ?? 0}
                onChange={(e) => onFormChange('makerFee', parseFloat(e.target.value) || 0)}
                step={feeMode === 'percent' ? '0.01' : '0.1'}
                min="0"
                className={inputClass}
                placeholder={feeMode === 'percent' ? '0.1' : '0'}
              />
            </div>

            {/* Taker fee */}
            <div>
              <label className={labelClass}>Taker Fee</label>
              <input
                type="number"
                value={formData.takerFee ?? 0}
                onChange={(e) => onFormChange('takerFee', parseFloat(e.target.value) || 0)}
                step={feeMode === 'percent' ? '0.01' : '0.1'}
                min="0"
                className={inputClass}
                placeholder={feeMode === 'percent' ? '0.15' : '0'}
              />
            </div>

            {/* Overlays */}
            <div>
              <label className={labelClass}>Overlays</label>
              <MultiSelectDropdown
                label="Overlays"
                icon={Layers}
                options={OVERLAY_OPTIONS}
                selected={formData.overlays || ['ema20', 'volume']}
                onChange={(next) => onFormChange('overlays', next)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {desktopPanel}
      {mobilePanel}
    </>
  );
}
